import { useState, useRef, useEffect, useCallback } from 'react';

// Module-level cache: prevents re-fetching when component remounts
const MAX_CACHE_SIZE = 50;
const audioCache = new Map<
	string,
	{ blobUrl: string; waveformData: number[]; decodedDuration: number }
>();
// Track in-flight fetches to avoid duplicate concurrent requests for the same URL
const pendingFetches = new Map<
	string,
	Promise<{ blobUrl: string; waveformData: number[]; decodedDuration: number }>
>();

const evictOldestCacheEntry = () => {
	if (audioCache.size < MAX_CACHE_SIZE) return;
	const firstKey = audioCache.keys().next().value;
	if (firstKey) {
		const entry = audioCache.get(firstKey);
		if (entry) {
			URL.revokeObjectURL(entry.blobUrl);
		}
		audioCache.delete(firstKey);
	}
};

const fetchAndDecode = (
	audioSrc: string,
	samples: number
): Promise<{ blobUrl: string; waveformData: number[]; decodedDuration: number }> => {
	return fetch(audioSrc)
		.then((response) => {
			if (!response.ok) {
				throw new Error('Failed to fetch audio: ' + response.status);
			}
			const contentType = response.headers.get('content-type') || 'audio/mpeg';
			return response.arrayBuffer().then((arrayBuffer) => {
				return { arrayBuffer: arrayBuffer, contentType: contentType };
			});
		})
		.then((result) => {
			const arrayBuffer = result.arrayBuffer;
			const blob = new Blob([arrayBuffer], { type: result.contentType });
			const blobUrl = URL.createObjectURL(blob);

			const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
			const audioContext = new AudioCtx();

			return audioContext
				.decodeAudioData(arrayBuffer)
				.then((audioBuffer) => {
					const rawData = audioBuffer.getChannelData(0);
					const blockSize = Math.floor(rawData.length / samples);
					const filteredData: number[] = [];

					for (let i = 0; i < samples; i++) {
						let sum = 0;
						for (let j = 0; j < blockSize; j++) {
							sum += Math.abs(rawData[i * blockSize + j]);
						}
						filteredData.push(sum / blockSize);
					}

					const max = Math.max.apply(null, filteredData);
					const waveformData =
						max > 0 ? filteredData.map((n) => (n / max) * 100) : filteredData.map(() => 0);

					audioContext.close();

					evictOldestCacheEntry();
					const cacheResult = {
						blobUrl: blobUrl,
						waveformData: waveformData,
						decodedDuration: audioBuffer.duration,
					};
					audioCache.set(audioSrc, cacheResult);
					pendingFetches.delete(audioSrc);
					return cacheResult;
				})
				.catch((decodeError) => {
					URL.revokeObjectURL(blobUrl);
					audioContext.close();
					throw decodeError;
				});
		})
		.catch((error) => {
			pendingFetches.delete(audioSrc);
			throw error;
		});
};

const makeDummyWaveform = (samples: number): number[] => {
	return Array(samples)
		.fill(0)
		.map(() => Math.random() * 30 + 20);
};

interface UseAudioPlaybackOptions {
	src: string;
	samples?: number;
}

interface UseAudioPlaybackReturn {
	isPlaying: boolean;
	isLoading: boolean;
	currentTime: number;
	duration: number;
	waveformData: number[];
	progress: number;

	togglePlayPause: () => void;
	seek: (percent: number) => void;
}

const useAudioPlayback = (options: UseAudioPlaybackOptions): UseAudioPlaybackReturn => {
	const src = options.src;
	const samples = options.samples || 50;

	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [waveformData, setWaveformData] = useState<number[]>([]);
	const [isLoading, setIsLoading] = useState(true);

	const audioRef = useRef<HTMLAudioElement | null>(null);
	const handlersRef = useRef<{
		loadedmetadata: (() => void) | null;
		timeupdate: (() => void) | null;
		ended: (() => void) | null;
		error: (() => void) | null;
	}>({ loadedmetadata: null, timeupdate: null, ended: null, error: null });

	// Fetch and decode audio, with module-level caching
	useEffect(() => {
		let cancelled = false;

		// Remove previous listeners from audio element
		const removeListeners = () => {
			const audio = audioRef.current;
			const handlers = handlersRef.current;
			if (audio) {
				if (handlers.loadedmetadata)
					audio.removeEventListener('loadedmetadata', handlers.loadedmetadata);
				if (handlers.timeupdate) audio.removeEventListener('timeupdate', handlers.timeupdate);
				if (handlers.ended) audio.removeEventListener('ended', handlers.ended);
				if (handlers.error) audio.removeEventListener('error', handlers.error);
			}
			handlersRef.current = { loadedmetadata: null, timeupdate: null, ended: null, error: null };
		};

		const setupAudio = (blobUrl: string, waveData: number[], decodedDuration: number) => {
			if (cancelled) return;

			// Clean up any existing listeners before adding new ones
			removeListeners();

			if (!audioRef.current) {
				audioRef.current = new Audio(blobUrl);
			} else {
				audioRef.current.src = blobUrl;
				setIsPlaying(false);
				setCurrentTime(0);
			}

			const audio = audioRef.current;

			const handleLoadedMetadata = () => {
				if (!cancelled) {
					const dur = isFinite(audio.duration) ? audio.duration : decodedDuration;
					setDuration(dur);
				}
			};
			const handleTimeUpdate = () => {
				if (!cancelled) setCurrentTime(audio.currentTime);
			};
			const handleEnded = () => {
				if (!cancelled) {
					setIsPlaying(false);
					setCurrentTime(0);
				}
			};
			const handleError = () => {
				if (!cancelled) {
					console.error('Audio element error for src:', src);
					setWaveformData(makeDummyWaveform(samples));
					setIsLoading(false);
				}
			};

			// Store references for cleanup
			handlersRef.current = {
				loadedmetadata: handleLoadedMetadata,
				timeupdate: handleTimeUpdate,
				ended: handleEnded,
				error: handleError,
			};

			audio.addEventListener('loadedmetadata', handleLoadedMetadata);
			audio.addEventListener('timeupdate', handleTimeUpdate);
			audio.addEventListener('ended', handleEnded);
			audio.addEventListener('error', handleError);

			setDuration(decodedDuration);
			setWaveformData(waveData);
			setIsLoading(false);
		};

		// Reset state immediately so stale data from the previous src is never shown
		setIsPlaying(false);
		setCurrentTime(0);
		setDuration(0);
		setWaveformData([]);
		setIsLoading(true);

		const loadAudio = () => {
			// Cache hit — use immediately, no fetch
			const cached = audioCache.get(src);
			if (cached) {
				setupAudio(cached.blobUrl, cached.waveformData, cached.decodedDuration);
				return;
			}

			// Join an existing in-flight fetch instead of starting a new one
			let pending = pendingFetches.get(src);
			if (!pending) {
				pending = fetchAndDecode(src, samples);
				pendingFetches.set(src, pending);
			}

			pending
				.then((result) => {
					if (cancelled) return;
					setupAudio(result.blobUrl, result.waveformData, result.decodedDuration);
				})
				.catch((error) => {
					if (!cancelled) {
						console.error('Error loading audio:', error);
						setWaveformData(makeDummyWaveform(samples));
						setIsLoading(false);
					}
				});
		};

		loadAudio();

		return () => {
			cancelled = true;
			removeListeners();
		};
	}, [src, samples]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (audioRef.current) {
				audioRef.current.pause();
				audioRef.current = null;
			}
		};
	}, []);

	const togglePlayPause = useCallback(() => {
		if (!audioRef.current) return;

		if (audioRef.current.paused) {
			audioRef.current.play();
			setIsPlaying(true);
		} else {
			audioRef.current.pause();
			setIsPlaying(false);
		}
	}, []);

	const seek = useCallback(
		(percent: number) => {
			if (!audioRef.current || duration === 0) return;
			const newTime = percent * duration;
			audioRef.current.currentTime = newTime;
			setCurrentTime(newTime);
		},
		[duration]
	);

	const progress = duration > 0 ? currentTime / duration : 0;

	return {
		isPlaying: isPlaying,
		isLoading: isLoading,
		currentTime: currentTime,
		duration: duration,
		waveformData: waveformData,
		progress: progress,

		togglePlayPause: togglePlayPause,
		seek: seek,
	};
};

export const formatAudioTime = (seconds: number): string => {
	if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
	const mins = Math.floor(seconds / 60);
	const secs = Math.floor(seconds % 60);
	return mins + ':' + (secs < 10 ? '0' : '') + secs;
};

export const formatDurationMs = (ms: number): string => {
	const totalSeconds = Math.floor(ms / 1000);
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	return minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
};

export default useAudioPlayback;
